import { useState, useRef, useEffect } from 'react';

interface SliderProps {
  beforeImage: string;
  afterImage: string;
}

function CompareSlider({ beforeImage, afterImage }: SliderProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  const handleStart = (clientX: number) => {
    setIsDragging(true);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    }
  };

  const handleEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      ref={containerRef}
      className="relative aspect-[3/4] rounded-3xl overflow-hidden cursor-ew-resize select-none shadow-2xl border-4 border-[#EA9D00]"
      onMouseMove={handleMouseMove}
      onMouseDown={(e) => handleStart(e.clientX)}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={(e) => handleStart(e.touches[0].clientX)}
      onTouchEnd={handleEnd}
    >
      {/* Imagem DEPOIS (base) */}
      <img
        src={afterImage}
        alt="Cabelo depois do tratamento Mix Express com cobertura perfeita"
        title="Depois - Cobertura natural e perfeita dos fios brancos"
        className="absolute inset-0 w-full h-full object-cover object-top"
        draggable={false}
      />

      {/* Imagem ANTES (sobreposta com clip) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={beforeImage}
          alt="Cabelo antes do tratamento Mix Express mostrando fios brancos"
          title="Antes - Fios brancos visíveis"
          className="absolute inset-0 w-full h-full object-cover object-top"
          style={{ width: containerRef.current ? containerRef.current.offsetWidth : '100%', maxWidth: 'none' }}
          draggable={false}
        />
      </div>

      {/* Linha divisória */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        {/* Botão do slider */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center border-4 border-[#EA9D00] transition-transform hover:scale-110">
          <i className="ri-arrow-left-s-line text-[#EA9D00] text-lg"></i>
          <i className="ri-arrow-right-s-line text-[#EA9D00] text-lg"></i>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 bg-gray-900/80 text-white px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide z-20">
        ANTES
      </div>
      <div className="absolute top-4 right-4 bg-[#EA9D00] text-white px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide z-20">
        DEPOIS
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [sliderPosition1, setSliderPosition1] = useState(50);
  const [sliderPosition2, setSliderPosition2] = useState(50);
  const [isDragging, setIsDragging] = useState<number | null>(null);
  const container1Ref = useRef<HTMLDivElement>(null);
  const container2Ref = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number, sliderId: number) => {
    const containerRef = sliderId === 1 ? container1Ref : container2Ref;
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    if (sliderId === 1) {
      setSliderPosition1(percentage);
    } else {
      setSliderPosition2(percentage);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging !== null) {
      handleMove(e.clientX, isDragging);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging !== null) {
      handleMove(e.touches[0].clientX, isDragging);
    }
  };

  const handleMouseDown = (e: React.MouseEvent, sliderId: number) => {
    setIsDragging(sliderId);
    handleMove(e.clientX, sliderId);
  };

  const handleTouchStart = (e: React.TouchEvent, sliderId: number) => {
    setIsDragging(sliderId);
    handleMove(e.touches[0].clientX, sliderId);
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  // Adicionar event listeners globais
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  const scrollToForm = () => {
    const formSection = document.getElementById('cadastro-form');
    formSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="bg-white py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#2A2A2A] mb-4 sm:mb-6">
            <span className="inline-flex items-center gap-3 sm:gap-4 flex-wrap justify-center">
              <span className="text-3xl sm:text-4xl">✨</span>
              <span>ANTES E DEPOIS</span>
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-[#2A2A2A] max-w-3xl mx-auto px-4">
            Veja a transformação real que o <strong className="text-[#EA9D00]">Mix Express</strong> proporciona
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {/* Comparador 1 */}
          <div className="bg-gray-50 rounded-3xl p-6 sm:p-8 shadow-lg">
            <div 
              className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden mb-6 cursor-ew-resize select-none" 
              ref={container1Ref}
              onMouseDown={(e) => handleMouseDown(e, 1)}
              onTouchStart={(e) => handleTouchStart(e, 1)}
            >
              {/* Imagem DEPOIS (base) */}
              <img
                src="https://cursos.llcosmeticos.com.br/mix/2.png"
                alt="Depois do Mix Express - Cobertura perfeita"
                title="Depois - Resultado natural e profissional"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
              {/* Imagem ANTES (sobreposta) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition1}%` }}
              >
                <img
                  src="https://cursos.llcosmeticos.com.br/mix/1.png"
                  alt="Antes do Mix Express - Cabelo com fios brancos"
                  title="Antes - Fios brancos visíveis"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ width: container1Ref.current ? `${container1Ref.current.offsetWidth}px` : '100%', maxWidth: 'none' }}
                  draggable={false}
                />
              </div>
              {/* Linha divisória */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-10"
                style={{ left: `${sliderPosition1}%`, transform: 'translateX(-50%)' }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full shadow-xl flex items-center justify-center border-4 border-[#EA9D00]">
                  <i className="ri-arrow-left-right-line text-[#EA9D00] text-lg sm:text-xl"></i>
                </div>
              </div>
              {/* Labels */}
              <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                ANTES
              </div>
              <div className="absolute top-4 right-4 bg-[#EA9D00] text-[#2A2A2A] px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                DEPOIS
              </div>
            </div>
            <p className="text-[#2A2A2A] text-sm sm:text-base text-center">
              Arraste o controle para ver a transformação
            </p>
          </div>

          {/* Comparador 2 */}
          <div className="bg-gray-50 rounded-3xl p-6 sm:p-8 shadow-lg">
            <div 
              className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden mb-6 cursor-ew-resize select-none" 
              ref={container2Ref}
              onMouseDown={(e) => handleMouseDown(e, 2)}
              onTouchStart={(e) => handleTouchStart(e, 2)}
            >
              {/* Imagem DEPOIS (base) */}
              <img
                src="https://cursos.llcosmeticos.com.br/mix/4.png"
                alt="Depois do Mix Express - Cobertura perfeita"
                title="Depois - Resultado natural e profissional"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
              {/* Imagem ANTES (sobreposta) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition2}%` }}
              >
                <img
                  src="https://cursos.llcosmeticos.com.br/mix/3.png"
                  alt="Antes do Mix Express - Cabelo com fios brancos"
                  title="Antes - Fios brancos visíveis"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ width: container2Ref.current ? `${container2Ref.current.offsetWidth}px` : '100%', maxWidth: 'none' }}
                  draggable={false}
                />
              </div>
              {/* Linha divisória */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-10"
                style={{ left: `${sliderPosition2}%`, transform: 'translateX(-50%)' }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full shadow-xl flex items-center justify-center border-4 border-[#EA9D00]">
                  <i className="ri-arrow-left-right-line text-[#EA9D00] text-lg sm:text-xl"></i>
                </div>
              </div>
              {/* Labels */}
              <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                ANTES
              </div>
              <div className="absolute top-4 right-4 bg-[#EA9D00] text-[#2A2A2A] px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                DEPOIS
              </div>
            </div>
            <p className="text-[#2A2A2A] text-sm sm:text-base text-center">
              Arraste o controle para ver a transformação
            </p>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={scrollToForm}
            className="bg-[#EA9D00] text-[#2A2A2A] font-bold text-sm sm:text-base md:text-lg px-6 sm:px-10 md:px-12 py-3 sm:py-4 rounded-full hover:bg-[#d89000] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            QUERO ESSES RESULTADOS - VAGA GRATUITA
          </button>
        </div>
      </div>
    </section>
  );
}
